import {
  Accordion,
  Avatar,
  Badge,
  Box,
  Card,
  Center,
  Divider,
  Group,
  Pagination,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title
} from '@mantine/core';
import {
  IconBook,
  IconSearch,
  IconUserQuestion,
  IconUsers,
  IconZoomQuestion
} from '@tabler/icons-react';
import { RESPONSES_TYPE_COLOR } from 'constant/responseTypeColor';
import React, { useEffect, useMemo, useState } from 'react';

interface QuestionData {
  type: 'Range' | 'Choose One' | 'Free-text';
  trAssessmentQuestionID: string;
  title: string;
  sequence: number;
  respondentType: 'Self' | 'Peer' | 'Supervisor' | 'Subordinate' | 'External';
  rangeItems: any[];
  description: string;
  answer: string;
  typeLabel?: string;
  category?: string;
  skalaCategory?: string;
}

interface ModuleData {
  moduleID: string;
  moduleName: string;
  trAssessmentModuleID: string;
  questions: QuestionData[];
}

interface ModuleSectionProps {
  moduleData: ModuleData[];
}

const QUESTION_TYPE_COLOR: Record<QuestionData['type'], string> = {
  Range: 'indigo',
  'Choose One': 'teal',
  'Free-text': 'gray'
};

const ModuleSection: React.FC<ModuleSectionProps> = ({ moduleData }) => {
  const [selectedModule, setSelectedModule] = useState<null | string>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [modules, setModules] = useState<ModuleData[]>([]);

  const itemsPerPage = 10;

  useEffect(() => {
    if (moduleData?.length > 0) {
      setModules(moduleData);
    }
    return () => {
      setModules([]);
    };
  }, [moduleData]);

  const filteredModule = useMemo(() => {
    if (!searchQuery) return modules;

    return modules.filter((response) => {
      const searchValue = searchQuery.toLowerCase();
      return (
        response?.moduleName?.toLowerCase().includes(searchValue) ||
        response?.moduleID?.toLowerCase().includes(searchValue)
      );
    });
  }, [modules, searchQuery]);

  const totalPages = Math.ceil(filteredModule.length / itemsPerPage) || 1;

  const paginatedModule = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredModule.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredModule, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const selectedModuleDetail: ModuleData | undefined = useMemo(
    () => modules.find((response) => response?.moduleID === selectedModule),
    [modules, selectedModule]
  );

  const moduleName = selectedModuleDetail?.moduleName || 'No Module available';

  // Function to group questions by typeLabel for External questions
  const getExternalQuestionGroups = (questions: QuestionData[]) => {
    const externalQuestions = questions.filter(
      (q) =>
        q.respondentType?.toLowerCase() === 'external' || q.title.toLowerCase().includes('external')
    );

    // Group external questions by typeLabel
    const groupedQuestions: Record<string, QuestionData[]> = {};

    externalQuestions.forEach((question) => {
      const typeLabel = question.typeLabel || 'General';
      if (!groupedQuestions[typeLabel]) {
        groupedQuestions[typeLabel] = [];
      }
      groupedQuestions[typeLabel].push(question);
    });

    return groupedQuestions;
  };

  const getGroupColor = (groupName: string) =>
    RESPONSES_TYPE_COLOR[groupName as keyof typeof RESPONSES_TYPE_COLOR] || '#adb5bd';

  const buildGroupValue = (label: string) => label.toLowerCase().replace(/\s+/g, '-');

  const { regularGroupEntries, externalGroupEntries } = useMemo(() => {
    if (!selectedModuleDetail?.questions) {
      return {
        regularGroupEntries: [] as Array<{ name: string; questions: QuestionData[] }>,
        externalGroupEntries: [] as Array<[string, QuestionData[]]>
      };
    }

    const groupOrder: string[] = [];
    const groups = new Map<string, QuestionData[]>();

    selectedModuleDetail.questions.forEach((question: QuestionData) => {
      if (question.respondentType?.toLowerCase() === 'external') return;

      let groupName: string;

      if (question.respondentType) {
        groupName = question.respondentType;
      } else if (question.title.includes('anda mengalami peningkatan')) {
        groupName = 'Self Assessment';
      } else if (question.title.includes('Tim saya mampu')) {
        groupName = 'Team Assessment';
      } else if (question.title.includes('karyawan dibawah ini mampu')) {
        groupName = 'Peer Assessment';
      } else {
        groupName = 'Other Questions';
      }

      if (!groups.has(groupName)) {
        groups.set(groupName, []);
        groupOrder.push(groupName);
      }

      groups.get(groupName)?.push(question);
    });

    const regularEntries = groupOrder.map((name) => ({ name, questions: groups.get(name) || [] }));
    const externalEntries = Object.entries(
      getExternalQuestionGroups(selectedModuleDetail.questions)
    );

    return { regularGroupEntries: regularEntries, externalGroupEntries: externalEntries };
  }, [selectedModuleDetail]);

  const externalQuestionsCount = useMemo(
    () => externalGroupEntries.reduce((acc, [, qs]) => acc + qs.length, 0),
    [externalGroupEntries]
  );

  const [openedGroups, setOpenedGroups] = useState<string[]>([]);
  const [externalOpenedGroups, setExternalOpenedGroups] = useState<string[]>([]);

  useEffect(() => {
    setOpenedGroups([]);
    setExternalOpenedGroups([]);
  }, [selectedModule]);

  const handleGroupChange = (values: string[]) => {
    setOpenedGroups(values);

    if (!values.includes('external-assessment')) {
      setExternalOpenedGroups([]);
    }
  };

  const renderQuestionCard = (
    question: QuestionData,
    index: number,
    accentColor: string
  ): React.ReactNode => {
    const typeColor = QUESTION_TYPE_COLOR[question.type] || 'gray';

    return (
      <Card
        key={question.trAssessmentQuestionID || index}
        withBorder
        radius='md'
        shadow='xs'
        p='sm'
        style={{
          borderLeft: `3px solid ${accentColor}`,
          borderColor: '#e9ecef',
          backgroundColor: '#f8f9fc'
        }}
      >
        <Group gap='md' align='flex-start'>
          <ThemeIcon radius='xl' size={28} variant='light' color={accentColor}>
            <Text fw={600} size='xs'>
              {index + 1}
            </Text>
          </ThemeIcon>
          <Stack gap={6} style={{ flex: 1 }}>
            <Group justify='space-between' align='flex-start' gap='sm'>
              <Text fw={600} size='sm' c='dark'>
                {question.title}
              </Text>
              <Badge size='xs' variant='light' color={typeColor}>
                {question.type}
              </Badge>
            </Group>

            {question.description && (
              <Text size='xs' c='dimmed'>
                {question.description}
              </Text>
            )}

            {(question.category || question.skalaCategory) && (
              <Group gap='sm' mt='xs'>
                {question.category && (
                  <Badge size='xs' variant='outline' color='orange'>
                    Category: {question.category}
                  </Badge>
                )}
                {question.skalaCategory && (
                  <Badge size='xs' variant='outline' color='violet'>
                    Skala: {question.skalaCategory}
                  </Badge>
                )}
              </Group>
            )}

            {question.type === 'Range' && question.rangeItems?.length > 0 && (
              <Stack gap={4} mt='xs'>
                <Text size='xs' c='dimmed' fw={600}>
                  Scale
                </Text>
                <Group gap={6} wrap='wrap'>
                  {[...question.rangeItems]
                    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
                    .map((item: any, idx: number) => (
                      <Badge
                        key={item.trAssessmentQuestionRangeItemID || idx}
                        size='xs'
                        variant='light'
                        color={
                          idx === 0
                            ? 'red'
                            : idx === question.rangeItems.length - 1
                              ? 'green'
                              : 'blue'
                        }
                      >
                        {item.label} ({item.value})
                      </Badge>
                    ))}
                </Group>
              </Stack>
            )}

            {question.type === 'Free-text' && (
              <Box
                mt='xs'
                style={{
                  padding: '8px 12px',
                  borderRadius: 6,
                  backgroundColor: '#f1f3f5',
                  fontStyle: 'italic'
                }}
              >
                <Text size='xs' c='dimmed'>
                  Free-text response field
                </Text>
              </Box>
            )}
          </Stack>
        </Group>
      </Card>
    );
  };

  return (
    <Stack gap='md'>
      <Card withBorder radius='lg' shadow='sm'>
        <Stack>
          <Group justify='space-between'>
            <Group gap={6}>
              <ThemeIcon variant='light' radius='md'>
                <IconUsers size={16} />
              </ThemeIcon>
              <Stack gap={0}>
                <Text c='dark' fw={600} size='sm'>
                  Module List
                </Text>
                <Text c='dimmed' size='xs'>
                  Select a module to view details
                </Text>
              </Stack>
            </Group>
            {!!modules.length && (
              <Badge variant='light' size='sm' radius='sm'>
                {modules.length} total
              </Badge>
            )}
          </Group>

          <TextInput
            placeholder='Search module name or ID'
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            radius='md'
            size='sm'
            variant='filled'
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />

          <ScrollArea mah={300} type='auto' offsetScrollbars>
            {modules.length === 0 ? (
              <Center py='xl'>
                <Text c='dimmed'>No modules found</Text>
              </Center>
            ) : (
              <Stack gap='xs' pr='sm' py='xs'>
                {paginatedModule?.map((response: ModuleData, index: number) => {
                  const isActive = selectedModule === response?.moduleID;
                  const questionCount = response?.questions?.length ?? 0;

                  return (
                    <Paper
                      key={response?.moduleID || index}
                      withBorder
                      radius='md'
                      shadow={isActive ? 'sm' : 'xs'}
                      px='md'
                      py='sm'
                      onClick={() => setSelectedModule(response?.moduleID)}
                      style={{
                        cursor: 'pointer',
                        transition: 'all 160ms ease',
                        borderColor: isActive ? '#4c6ef5' : '#e9ecef',
                        backgroundColor: isActive ? '#edf2ff' : '#ffffff',
                        transform: isActive ? 'translateY(-1px)' : 'none'
                      }}
                    >
                      <Stack gap={4}>
                        <Group justify='space-between' align='center'>
                          <Text fw={600} c='dark' size='sm'>
                            {response?.moduleName || `Module ${index + 1}`}
                          </Text>
                          <Badge size='xs' variant={isActive ? 'filled' : 'light'} color='blue'>
                            {questionCount} questions
                          </Badge>
                        </Group>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </ScrollArea>

          {modules.length > itemsPerPage && <Divider />}

          {modules.length > itemsPerPage && (
            <Pagination
              total={totalPages}
              value={currentPage}
              onChange={(page) => setCurrentPage(page)}
              size='xs'
              radius='md'
            />
          )}
        </Stack>
      </Card>

      <Card withBorder radius='lg' shadow='sm' p='md'>
        {!selectedModule && (
          <Center py={'xl'}>
            <Stack align='center'>
              <Avatar color='primary' radius='xl' size='xl'>
                <IconUserQuestion size={48} />
              </Avatar>
              <Stack align='center' gap={2}>
                <Title order={2} c='dark'>
                  No module selected
                </Title>
                <Text ta={'center'} c={'dimmed'} fz={'sm'} maw={360}>
                  To start viewing responses, select a module from the module list to view their
                  detailed data.
                </Text>
              </Stack>
            </Stack>
          </Center>
        )}
        {selectedModule && (
          <>
            <Stack gap='xs'>
              <Text size='lg' fw={700} c='dark'>
                {moduleName}
              </Text>
              <Group gap='xs'>
                <Badge size='sm' variant='light' color='teal'>
                  {selectedModuleDetail?.questions?.length || 0} questions
                </Badge>
              </Group>
            </Stack>
            <Divider my='md' />

            <Group mb={16} gap='xs'>
              <ThemeIcon variant='light' color='blue' radius='md'>
                <IconBook size={14} />
              </ThemeIcon>
              <Text fw={600} fz={16}>
                Assessment Questions
              </Text>
            </Group>

            {selectedModuleDetail?.questions.length === 0 && (
              <Stack gap={2} align='center'>
                <Avatar color='primary' radius='xl' size='xl'>
                  <IconZoomQuestion size={48} />
                </Avatar>
                <Title order={3} c='dark'>
                  No questions found
                </Title>
                <Text ta={'center'} c={'dimmed'} fz={'sm'}>
                  No questions found for this module.
                </Text>
              </Stack>
            )}

            {selectedModuleDetail?.questions && selectedModuleDetail?.questions.length > 0 && (
              <Accordion
                multiple
                value={openedGroups}
                onChange={handleGroupChange}
                radius='md'
                variant='separated'
                styles={{
                  item: {
                    borderRadius: 12
                  },
                  control: {
                    padding: '10px 14px'
                  },
                  content: {
                    padding: '8px 0 12px'
                  }
                }}
              >
                {regularGroupEntries.map(({ name, questions }) => {
                  const color = getGroupColor(name);
                  const value = buildGroupValue(name);

                  return (
                    <Accordion.Item key={value} value={value}>
                      <Accordion.Control>
                        <Group gap='sm'>
                          <Box
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: color
                            }}
                          />
                          <Text fw={600}>
                            {name} ({questions.length})
                          </Text>
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap='sm'>
                          {questions.map((question, index) =>
                            renderQuestionCard(question, index, color)
                          )}
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  );
                })}

                {externalGroupEntries.length > 0 && (
                  <Accordion.Item key='external-assessment' value='external-assessment'>
                    <Accordion.Control>
                      <Group gap='sm'>
                        <Box
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: '#6741d9'
                          }}
                        />
                        <Text fw={600}>External Questions ({externalQuestionsCount})</Text>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Accordion
                        multiple
                        value={externalOpenedGroups}
                        onChange={setExternalOpenedGroups}
                        radius='md'
                        variant='filled'
                      >
                        {externalGroupEntries.map(([typeLabel, questions]) => (
                          <Accordion.Item
                            key={`external-${typeLabel.toLowerCase().replace(/\s+/g, '-')}`}
                            value={typeLabel}
                          >
                            <Accordion.Control>
                              <Group gap='sm'>
                                <Box
                                  style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: '#7950f2'
                                  }}
                                />
                                <Text fw={500}>
                                  {typeLabel} ({questions.length})
                                </Text>
                              </Group>
                            </Accordion.Control>
                            <Accordion.Panel>
                              <Stack gap='sm'>
                                {questions.map((question, index) =>
                                  renderQuestionCard(question, index, '#7950f2')
                                )}
                              </Stack>
                            </Accordion.Panel>
                          </Accordion.Item>
                        ))}
                      </Accordion>
                    </Accordion.Panel>
                  </Accordion.Item>
                )}
              </Accordion>
            )}
          </>
        )}
      </Card>
    </Stack>
  );
};

export default ModuleSection;
